// This file is part of Moodle - http://moodle.org/
// Moodle is free software, distributed under the GNU GPL v3 or later.

/**
 * Non-destructive content interaction controls.
 *
 * @module local_disablerightclick/engine
 * @copyright 2020 Yogesh Shirsath <yogshirsath@hotmail.com>
 * @license http://www.gnu.org/copyleft/gpl.html GNU GPL v3 or later
 */

import {restrictionsFor, isEditing, interactionFeature} from './policy';

const documentBinding = Symbol.for('local_disablerightclick.document');
const controllerBinding = Symbol.for('local_disablerightclick.controller');

/** @returns {Boolean} Whether this document already owns its policy. */
export const isActive = () => Boolean(document[controllerBinding]);

/** Remove the controller shared by the early script and AMD fallback. */
export const destroy = () => {
    const current = document[controllerBinding];
    if (current) {
        delete document[controllerBinding];
        current();
    }
};

/**
 * Apply one policy, following accessible iframe documents without polling.
 * @param {Object} settings Normalized settings.
 * @param {String[]} strings Localized messages.
 * @returns {Function} Cleanup.
 */
const install = (settings, strings) => {
    const enabled = {
        contextmenu: settings.disablerightclick,
        clipboard: settings.disablecutcopypaste,
        shortcuts: settings.disabledevelopertools,
    };
    const messageIndexes = {contextmenu: 0, clipboard: 1, shortcuts: 2};
    let toast = null;
    let hideTimer = null;
    let messageTimer = null;
    const onPageHide = event => {
        if (!event.persisted) {
            destroy();
        }
    };

    /**
     * Announce a restriction in the host page, without moving focus.
     * @param {String} message Localized plain text.
     */
    const showMessage = message => {
        if (!document.body || typeof message !== 'string' || !message) {
            return;
        }
        if (!toast || !toast.isConnected) {
            toast = document.createElement('div');
            toast.className = 'local-disablerightclick-toast';
            toast.setAttribute('role', 'status');
            toast.setAttribute('aria-live', 'polite');
            toast.setAttribute('aria-atomic', 'true');
            document.body.appendChild(toast);
        }
        window.clearTimeout(hideTimer);
        window.clearTimeout(messageTimer);
        toast.textContent = '';
        messageTimer = window.setTimeout(() => {
            if (toast) {
                toast.textContent = message;
                toast.classList.add('is-visible');
            }
        }, 0);
        hideTimer = window.setTimeout(() => {
            if (toast) {
                toast.classList.remove('is-visible');
                toast.textContent = '';
            }
        }, 3000);
    };

    /**
     * Own a document only until it navigates, is removed, or starts its own Moodle controller.
     * @param {Document} doc Accessible document.
     * @param {Function} inheritedUrl Resolve the current parent URL for about:blank/srcdoc documents.
     * @param {Boolean} root Whether the document is the current Moodle page.
     * @returns {Object|null} Binding owned by this instance.
     */
    const bindDocument = (doc, inheritedUrl, root = false) => {
        const existing = doc[documentBinding];
        if (existing) {
            if (root && !existing.root) {
                existing.dispose();
            } else {
                existing.refresh();
                return null;
            }
        }
        const frames = new Map();
        const events = ['contextmenu', 'keydown', 'copy', 'cut', 'paste'];
        let observer = null;
        let disposed = false;
        let previousAddress = null;
        let previousPolicy = null;

        /**
         * Resolve the live document address, including changes made with history APIs.
         * @returns {String}
         */
        const address = () => {
            const current = doc.URL;
            return current === 'about:blank' || current.startsWith('about:srcdoc') ? inheritedUrl() : current;
        };

        /**
         * Rules are immutable for this binding; re-evaluate only when its effective URL changes.
         * @returns {Object} Current independent feature decisions.
         */
        const currentPolicy = () => {
            const current = address();
            if (current !== previousAddress) {
                previousPolicy = restrictionsFor(current, settings);
                previousAddress = current;
            }
            return previousPolicy;
        };

        /**
         * Restrict displayed content only; never remove nodes or affect page availability.
         * @param {Event} event User interaction.
         */
        const onEvent = event => {
            if (event.defaultPrevented || !event.cancelable) {
                return;
            }
            const feature = interactionFeature(event);
            if (!feature || !enabled[feature] || (feature !== 'shortcuts' && isEditing(event, doc))) {
                return;
            }
            if (currentPolicy()[feature]) {
                event.preventDefault();
                if (!event.repeat) {
                    showMessage(strings[messageIndexes[feature]]);
                }
            }
        };

        /**
         * Stop watching a removed iframe.
         * @param {HTMLIFrameElement} frame Frame element.
         * @param {Object} entry Frame listener and document.
         */
        const releaseFrame = (frame, entry) => {
            ['load', 'pointerenter', 'focus'].forEach(type => frame.removeEventListener(type, entry.sync));
            if (entry.binding) {
                entry.binding.dispose();
            }
            frames.delete(frame);
        };

        /**
         * Follow accessible document replacements, respecting the same-origin policy.
         * @param {HTMLIFrameElement} frame Frame element.
         */
        const watchFrame = frame => {
            if (frames.has(frame)) {
                return;
            }
            const entry = {doc: null, binding: null, sync: null};
            entry.sync = () => {
                let child = null;
                try {
                    child = frame.contentDocument;
                } catch (error) {
                    // Browser-origin restrictions are preserved.
                }
                if (entry.doc === child) {
                    // Rewriting with document.open() retains the Document but removes its event listeners.
                    if (child && child[documentBinding]) {
                        child[documentBinding].refresh();
                    }
                    return;
                }
                if (entry.binding) {
                    entry.binding.dispose();
                }
                entry.doc = child;
                entry.binding = child && child.documentElement ? bindDocument(child, address) : null;
            };
            frames.set(frame, entry);
            ['load', 'pointerenter', 'focus'].forEach(type => frame.addEventListener(type, entry.sync));
            entry.sync();
        };

        /**
         * Discover only frames in a newly inserted subtree.
         * @param {Node} node Added node or initial document.
         */
        const discover = node => {
            if (node.nodeType === 1 && node.tagName === 'IFRAME') {
                watchFrame(node);
            }
            if (node.querySelectorAll) {
                node.querySelectorAll('iframe').forEach(watchFrame);
            }
        };

        // A focused child can be interactive before its slow resources finish loading.
        const refreshFrames = () => frames.forEach(entry => entry.sync());

        const binding = {
            root,
            element: doc.documentElement,
            refresh: () => {
                if (!disposed) {
                    binding.element = doc.documentElement;
                    if (root) {
                        // The document.open() call also clears window listeners; restore lifecycle cleanup.
                        window.addEventListener('pagehide', onPageHide);
                    }
                    if (settings.disablerightclick || settings.disablecutcopypaste || settings.disabledevelopertools) {
                        // Re-adding the same listener is idempotent, including after a document rewrite.
                        events.forEach(type => doc.addEventListener(type, onEvent, true));
                        doc.defaultView?.addEventListener('blur', refreshFrames);
                        discover(doc);
                    }
                }
            },
            dispose: () => {
                if (disposed) {
                    return;
                }
                disposed = true;
                if (observer) {
                    observer.disconnect();
                }
                doc.defaultView?.removeEventListener('blur', refreshFrames);
                events.forEach(type => doc.removeEventListener(type, onEvent, true));
                frames.forEach((entry, frame) => releaseFrame(frame, entry));
                if (doc[documentBinding] === binding) {
                    delete doc[documentBinding];
                }
            },
        };
        doc[documentBinding] = binding;
        try {
            if (settings.disablerightclick || settings.disablecutcopypaste || settings.disabledevelopertools) {
                observer = new MutationObserver(records => {
                    if (binding.element !== doc.documentElement) {
                        binding.refresh();
                    }
                    records.forEach(record => record.addedNodes.forEach(discover));
                    if (frames.size && records.some(record => record.removedNodes.length)) {
                        frames.forEach((entry, frame) => {
                            if (!frame.isConnected || frame.ownerDocument !== doc) {
                                releaseFrame(frame, entry);
                            }
                        });
                    }
                });
                observer.observe(doc, {childList: true, subtree: true});
                binding.refresh();
            }
        } catch (error) {
            binding.dispose();
            throw error;
        }
        return binding;
    };

    const root = bindDocument(document, () => document.URL, true);
    window.addEventListener('pagehide', onPageHide);
    return () => {
        window.removeEventListener('pagehide', onPageHide);
        if (root) {
            root.dispose();
        }
        window.clearTimeout(hideTimer);
        window.clearTimeout(messageTimer);
        if (toast) {
            toast.remove();
            toast = null;
        }
    };
};

/**
 * Start synchronously; both loaders share one document controller.
 * @param {Object} settings Normalized policy.
 * @param {String[]} strings Mutable localized notification strings.
 * @returns {Boolean} Whether the policy is active.
 */
export const activate = (settings, strings) => {
    if (!isActive()) {
        document[controllerBinding] = install(settings, strings);
    }
    return true;
};
